import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import { useEffect, useRef, useState } from "react";
import { CustomDateFilterButton } from "../../common/components/button/CustomDateFilterButton";
import { TicketDetailModal } from "./modals/TicketDetailModal";
import { CustomTable, LinkTd, StatusBadge } from "../../common/components/table/CustomTable";
import SearchBar from "../../common/components/input/SearchBar";
import SecondaryButton from "../../common/components/button/SecondaryButton";
import PrimaryButton from "../../common/components/button/PrimaryButton";
import Translate from "../../common/translate/Translate";
import CategoryFilterButton from "../../common/components/filter_button/CategoryFilterButton";
import IssueTypeFilterButton from "../../common/components/filter_button/IssueTypeFilterButton";
import { useDevice } from "../../common/hooks/useDevice";
import { useWindowSize } from "../../common/hooks/useWindowSize";
import TicketService from "../../service/TicketService";
import { useSearchParams } from "react-router-dom";
import ExcelUtil from "../../common/utils/ExcelUtil";

export function TicketListPage() {
    const service = new TicketService();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page") || 1;

    const [dateFilter, setDateFilter] = useState("All Date");
    const [statusFilter, setStatusFilter] = useState(["all"]);
    const [type, setType] = useState(["all"]);
    const [searchFilter, setSearchFilter] = useState("ticketNo");
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [ticketList, setTicketList] = useState([]);
    const [detailDto, setDetailDto] = useState({
        ticketNo: "",
        shopName: "",
        shopCode: "",
        issueType: "",
        customerName: "",
        contact: "",
        detail: "",
        status: "Registered",
        recipient: "",
        deviceId: "",
        device: { deviceNumber: "-", type: "-", model: "-", deviceId: "-" },
        registeredDate: "",
        memos: [],
        image: [],
    });
    const [saveMode, setSaveMode] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchTicketList = (page, pageSize) => {
        service
            .getTicketList({ page: page, pageSize: pageSize })
            .then((result) => {
                if (!isMountedRef.current) return;
                const existingParams = Object.fromEntries(searchParams.entries());
                setSearchParams(
                    {
                        ...existingParams,
                        totalPage: result.page.total,
                    },
                    { replace: true }
                );
                setTicketList(result.ticketInfo);
            })
            .catch((error) => {
                console.error("Error fetching ticket list:", error);
            });
    };

    const fetchTicketDetail = (ticketNo) => {
        service
            .getTicketDetail({ ticketNo: ticketNo })
            .then((result) => {
                setDetailDto({
                    ticketNo: result.ticketNo || "",
                    shopName: result.shopName || "",
                    shopCode: result.shopCode || "",
                    storeId: result.storeId || "",
                    issueType: result.issueType || "",
                    customerName: result.customerName || "",
                    contact: result.contact || "",
                    detail: result.detail || "",
                    status: result.status || "Registered",
                    recipient: result.recipient || "",
                    deviceId: result.deviceId || "",
                    device: { deviceNumber: "-", type: "-", model: "-", deviceId: "-" },
                    registeredDate: result.registeredDate || "",
                    completedDate: result.completedDate || "",
                    memos: result.memos || [],
                    image: result.image || [],
                });
                setSaveMode(false);
                setOpenDetailModal(true);
            })
            .catch((error) => {
                console.error("Error fetching ticket detail:", error);
            });
    };

    useEffect(() => {
        fetchTicketList(page, 20);
    }, [page]);

    const handleSave = () => {
        fetchTicketList(page, 20);
    };

    const tableHeaders = [
        {
            key: "ticketNo",
            label: Translate("ticket.ticketNo"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        fetchTicketDetail(row.ticketNo);
                    }}
                >
                    {row.ticketNo}
                </LinkTd>
            ),
        },
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
        },
        {
            key: "shopName",
            label: Translate("store.shopName"),
        },
        {
            key: "issueType",
            label: Translate("ticket.type"),
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusBadge>{row.status}</StatusBadge>,
        },
        {
            key: "recipient",
            label: "Recipient",
        },
        {
            key: "registeredDate",
            label: "Registered Date",
        },
        {
            key: "completedDate",
            label: "Completed Date",
        },
    ];

    const handleExportExcel = () => {
        const columns = [
            { key: "ticketNo", label: Translate("ticket.ticketNo") },
            { key: "shopCode", label: Translate("store.shopCode") },
            { key: "shopName", label: Translate("store.shopName") },
            { key: "issueType", label: Translate("ticket.type") },
            { key: "status", label: "Status" },
            { key: "recipient", label: "Recipient" },
            { key: "registeredDate", label: "Registered Date" },
            { key: "completedDate", label: "Completed Date" },
        ];
        ExcelUtil.download({ fileName: "ticket_list", columns, rows: ticketList });
    };

    const handleAddTicket = () => {
        setSaveMode(true);
        setDetailDto({
            ticketNo: "",
            shopName: "",
            shopCode: "",
            storeId: "",
            issueType: "",
            customerName: "",
            contact: "",
            detail: "",
            status: "Registered",
            recipient: "",
            deviceId: "",
            device: { deviceNumber: "-", type: "-", model: "-", deviceId: "-" },
            registeredDate: "-",
            memos: [],
            image: [],
        });
        setOpenDetailModal(true);
    };

    const currentDevice = useDevice();
    const { width } = useWindowSize();

    const searchBar = (
        <SearchBar
            type={"ticketList"}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            totalWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "100%" : undefined}
            selectorWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "20%" : undefined}
            inputWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "70%" : undefined}
        />
    );

    const filterButtons = (
        <>
            <CustomDateFilterButton currentValue={dateFilter} setValue={setDateFilter} />
            <SizedBox width={"10px"} />
            <CategoryFilterButton currentValues={statusFilter} setValues={setStatusFilter} type={"ticket"} />
            <SizedBox width={"10px"} />
            <IssueTypeFilterButton currentValues={type} setValues={setType} />
        </>
    );

    let exportExcelButton = (
        <SecondaryButton icon={"download"} text={Translate("common.exportExcel")} onClick={handleExportExcel} />
    );
    let addButton = <PrimaryButton icon={"add_white"} text={Translate("ticket.addTicket")} onClick={handleAddTicket} />;

    if (currentDevice === "mobile" || currentDevice === "tablet" || width <= 1238) {
        addButton = <PrimaryButton icon={"add_white"} onClick={handleAddTicket} onlyIcon />;
        exportExcelButton = <></>;
    }

    let pageTitle = <PageTitle title={Translate("gnb.ticket")} contentLength={ticketList.length} />;

    if (currentDevice === "mobile" || currentDevice === "tablet") {
        pageTitle = (
            <PageTitle
                title={Translate("gnb.storeList")}
                contentLength={ticketList.length}
                button={currentDevice === "mobile" || currentDevice === "tablet" ? addButton : undefined}
            />
        );
    }

    let headerSection = (
        <>
            <PageTitle title={Translate("gnb.ticket")} contentLength={ticketList.length} />
            <SizedBox height={"24px"} />
            <Row justifyContent={"space-between"}>
                <Row>
                    {searchBar}
                    <SizedBox width={"10px"} />
                    {filterButtons}
                </Row>
                <Row>
                    {exportExcelButton}
                    <SizedBox width={"13px"} />
                    {addButton}
                </Row>
            </Row>
            <SizedBox height={"20px"} />
        </>
    );

    if (currentDevice === "mobile" || currentDevice === "tablet") {
        headerSection = (
            <>
                {pageTitle}
                <SizedBox height={"20px"} />
                {searchBar}
                <SizedBox height={"12px"} />
                <Row>{filterButtons}</Row>
                <SizedBox height={"20px"} />
            </>
        );
    }

    return (
        <>
            <CommonContentWrapper hasPagination>
                <Padding padding={"24px 20px"}>
                    {headerSection}
                    <CustomTable headers={tableHeaders} data={ticketList} />
                </Padding>
            </CommonContentWrapper>
            {openDetailModal && (
                <TicketDetailModal
                    isOpen={openDetailModal}
                    onClose={() => setOpenDetailModal(false)}
                    dto={detailDto}
                    isSaveMode={saveMode}
                    onSave={handleSave}
                />
            )}
        </>
    );
}
